import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { t } from "ttag";
import z from "zod";

import type { EventPrefix } from "@/features/analytics/models";
import { EVENT_PREFIX } from "@/features/analytics/models";
import {
  ANALYTICS_PARAMS,
  MOUSEFLOW_FORM_SUBMIT,
} from "@/features/analytics/models/constants";
import { trackPaymentCompleted } from "@/features/analytics/trackers/payment-completed";
import { trackPaymentFailed } from "@/features/analytics/trackers/payment-failed";
import { triggerMouseflowFormSubmit } from "@/features/analytics/triggers";
import type { IntakeFormPayload } from "@/features/billing/api";
import {
  useCompleteOnboarding,
  useOnboardingUser,
} from "@/features/onboarding/hooks";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { getPricesIds } from "@/features/onboarding/utils";
import { paymentsQueriesStore } from "@/features/payments/api";
import {
  PAYMENT_REDIRECT_METHOD_KEY,
  PAYMENT_REDIRECT_STATUS,
  PAYMENT_REDIRECT_STATUS_KEY,
  PaymentMethodType,
  SUBSCRIPTION_STATUS,
} from "@/shared/domains";
import { errorToast } from "@/shared/ui";

export const useHandlePaymentRedirect = ({
  eventPrefix = EVENT_PREFIX.NONE,
  intakeForm,
  onNeedsWhiteCoatSync,
  formId,
}: {
  eventPrefix?: EventPrefix;
  intakeForm?: IntakeFormPayload;
  onNeedsWhiteCoatSync?: VoidFunction;
  formId?: string;
} = {}) => {
  const [searchParams] = useSearchParams();

  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const checkoutDiscount = useOnboardingStore(
    (state) => state.checkoutDiscount,
  );
  const metadata = useOnboardingStore((state) => state.urlParams);
  const isCallCenter = useOnboardingStore((state) => state.isCallCenter);

  const user = useOnboardingUser();

  const redirectStatus = z
    .enum(PAYMENT_REDIRECT_STATUS)
    .safeParse(searchParams.get(PAYMENT_REDIRECT_STATUS_KEY)).data;
  const isRedirect = Boolean(redirectStatus);
  const paymentMethod = PaymentMethodType.safeParse(
    searchParams.get(PAYMENT_REDIRECT_METHOD_KEY),
  ).data;
  const hasCompletedOnboarding = useRef(false);

  const completeOnboardingMutation = useCompleteOnboarding({
    onNeedsWhiteCoatSync,
  });

  const { data: recentPurchases, isLoading: isLoadingRecentPurchases } =
    useQuery({
      ...paymentsQueriesStore.recent(user?.code),
      enabled: redirectStatus === PAYMENT_REDIRECT_STATUS.SUCCEEDED,
    });

  useEffect(() => {
    if (formId && redirectStatus === PAYMENT_REDIRECT_STATUS.FAILED) {
      triggerMouseflowFormSubmit({
        submit: MOUSEFLOW_FORM_SUBMIT.FAILURE,
        formId,
      });
    }
  }, [formId, redirectStatus]);

  useEffect(() => {
    if (
      !isRedirect ||
      !user ||
      !recentPurchases ||
      redirectStatus === PAYMENT_REDIRECT_STATUS.FAILED
    ) {
      return;
    }

    const hasActiveRecentPurchases = recentPurchases.some(
      (purchase) => purchase.subscription.status === SUBSCRIPTION_STATUS.ACTIVE,
    );

    if (!hasActiveRecentPurchases) {
      if (formId) {
        triggerMouseflowFormSubmit({
          submit: MOUSEFLOW_FORM_SUBMIT.FAILURE,
          formId,
        });
      }
      trackPaymentFailed({
        user,
        categories: selectedCategories,
        eventPrefix,
      });
      return errorToast(
        t`There was an issue processing your payment. Please try again using another payment method.`,
      );
    }

    if (hasCompletedOnboarding.current) {
      return;
    }
    hasCompletedOnboarding.current = true;
    if (formId) {
      triggerMouseflowFormSubmit({
        submit: MOUSEFLOW_FORM_SUBMIT.SUCCESS,
        formId,
      });
    }

    trackPaymentCompleted({
      user,
      categories: selectedCategories,
      metadata,
      eventPrefix,
      succeededInvoiceIds: recentPurchases.map(
        (purchase) => purchase.invoiceId ?? "",
      ),
      succeededPaymentIds: recentPurchases.map(
        (purchase) => purchase.paymentIntentId ?? "",
      ),
    });

    const efTransactionId = metadata[ANALYTICS_PARAMS.EF_TRANSACTION_ID];
    const affid = metadata[ANALYTICS_PARAMS.EVERFLOW_AFFILIATE_ID]?.toString();
    const oid = metadata[ANALYTICS_PARAMS.EVERFLOW_ORDER_ID]?.toString();

    completeOnboardingMutation.mutate(
      {
        code: user.code,
        state: user.state,
        shipping: user.shipping,
        phoneNumber: user.phoneNumber,
        discount: checkoutDiscount?.code,
        ...(intakeForm && { intakeForm }),
        ...(efTransactionId &&
          affid &&
          oid && {
            everflowParams: {
              efTransactionId,
              affid,
              oid,
              prices: getPricesIds(selectedCategories),
            },
          }),
        isCallCenter,
      },
      {
        onError: () => {
          hasCompletedOnboarding.current = false;
        },
      },
    );
  }, [recentPurchases]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isRedirect,
    isVerifying:
      isLoadingRecentPurchases || completeOnboardingMutation.isPending,
    paymentMethod,
  };
};
